import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { selectAuthStatus } from '../store/auth/auth.selectors';
import * as AuthActions from '../store/auth/auth.actions';
import { decodeJwtClaims, isJwtExpired } from '../core/auth/jwt';

export const AuthGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
    const store = inject(Store);
    const router = inject(Router);

    const snapshot = await firstValueFrom(
        store.select(selectAuthStatus).pipe(
            take(1),
            catchError(() => of(null as any))
        )
    );

    if (snapshot?.isAuthenticated === true && snapshot?.accessToken) {
        const claims = decodeJwtClaims(snapshot.accessToken);
        if (claims && !isJwtExpired(claims)) return true;
    }
    if (snapshot?.isAuthenticated === false) {
        return router.parseUrl(`/login?returnUrl=${encodeURIComponent(state.url)}`);
    }

    store.dispatch(AuthActions.checkAuth({ force: false }));

    const resolved = await firstValueFrom(
        store.select(selectAuthStatus).pipe(
            filter((s) => s.isAuthenticated !== null && !s.loading),
            take(1),
            timeout(5000),
            catchError(() => of({ isAuthenticated: false, accessToken: null } as any))
        )
    );

    if (resolved?.isAuthenticated === true && resolved?.accessToken) return true;
    return router.parseUrl(`/login?returnUrl=${encodeURIComponent(state.url)}`);
};
